import { useNavigate } from "../contexts/AppContext";
import { PublicLayout } from "./PublicLayout";

export function Landing() {
  const navigate = useNavigate();

  const go = (path: string) => (e: Event) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section class="py-5 landing-hero">
        <div class="container text-center py-4">
          <h1 class="display-4 fw-bold mb-3 mh-4">
            List your links, get your portal{" "}
            <span class="text-primary">live</span>.
          </h1>
          <p class="lead text-muted mb-4 mx-auto text-center">
            Build and maintain your link page by chatting with... 🥁 <b>AI</b>{" "}
            🥁 (who knew!).
          </p>
          <div class="d-flex flex-column flex-sm-row gap-2 justify-content-center mb-3">
            <a
              class="btn btn-primary btn-lg"
              href="/signup"
              onClick={go("/signup")}
            >
              Create your page &mdash; free
            </a>
            <a
              class="btn btn-outline-secondary btn-lg"
              href="/pricing"
              onClick={go("/pricing")}
            >
              See pricing
            </a>
          </div>
          <p class="text-muted small">Free plan, no card needed.</p>
        </div>
      </section>

      {/* How it works */}
      <section class="py-5 bg-body-secondary">
        <div class="container">
          <h2 class="h4 text-center fw-semibold mb-5">How it works</h2>
          <div class="row g-4 text-center justify-content-center">
            <div class="col-sm-4 col-lg-3">
              <div class="fs-1 mb-2">
                <i class="bi bi-layout-text-sidebar text-primary"></i>
              </div>
              <h3 class="h6 fw-semibold">1. Pick a starting template</h3>
              <p class="text-muted small mb-0">
                Choose a look that fits your vibe. Every template is a starting
                point, not a cage.
              </p>
            </div>
            <div class="col-sm-4 col-lg-3">
              <div class="fs-1 mb-2">
                <i class="bi bi-chat-left-text text-primary"></i>
              </div>
              <h3 class="h6 fw-semibold">2. Chat to build it out</h3>
              <p class="text-muted small mb-0">
                Tell the AI what links to add, what to change, or just describe
                a vibe. No forms, no code.
              </p>
            </div>
            <div class="col-sm-4 col-lg-3">
              <div class="fs-1 mb-2">
                <i class="bi bi-share text-primary"></i>
              </div>
              <h3 class="h6 fw-semibold">3. Publish and share</h3>
              <p class="text-muted small mb-0">
                One click. Your page is live at your own link, ready to put in
                every bio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section class="py-5">
        <div class="container">
          <h2 class="h4 text-center fw-semibold mb-5">
            Everything you need in one link
          </h2>
          <div class="row g-4">
            <div class="col-sm-6 col-lg-4">
              <div class="d-flex gap-3">
                <i class="bi bi-robot fs-4 text-primary flex-shrink-0 mt-1"></i>
                <div>
                  <h3 class="h6 fw-semibold mb-1">AI chat editing</h3>
                  <p class="text-muted small mb-0">
                    Change anything by asking. Add a link, restyle the page, or
                    rewrite your bio — just type it.
                  </p>
                </div>
              </div>
            </div>
            <div class="col-sm-6 col-lg-4">
              <div class="d-flex gap-3">
                <i class="bi bi-palette fs-4 text-primary flex-shrink-0 mt-1"></i>
                <div>
                  <h3 class="h6 fw-semibold mb-1">
                    Beautiful templates &amp; curated fonts
                  </h3>
                  <p class="text-muted small mb-0">
                    Start from a polished template and the AI picks fonts that
                    actually go together.
                  </p>
                </div>
              </div>
            </div>
            <div class="col-sm-6 col-lg-4">
              <div class="d-flex gap-3">
                <i class="bi bi-link-45deg fs-4 text-primary flex-shrink-0 mt-1"></i>
                <div>
                  <h3 class="h6 fw-semibold mb-1">Instant publishing</h3>
                  <p class="text-muted small mb-0">
                    Your page is live at your own link the moment you publish.
                    No hosting to configure.
                  </p>
                </div>
              </div>
            </div>
            <div class="col-sm-6 col-lg-4">
              <div class="d-flex gap-3">
                <i class="bi bi-play-circle fs-4 text-muted flex-shrink-0 mt-1"></i>
                <div>
                  <h3 class="h6 fw-semibold mb-1">
                    Embeds{" "}
                    <span
                      class="badge text-bg-secondary fw-normal"
                      style="font-size: 0.65rem"
                    >
                      Pro
                    </span>
                  </h3>
                  <p class="text-muted small mb-0">
                    Drop in YouTube videos, Calendly booking widgets, Stripe buy
                    buttons, and more.
                  </p>
                </div>
              </div>
            </div>
            <div class="col-sm-6 col-lg-4">
              <div class="d-flex gap-3">
                <i class="bi bi-bar-chart fs-4 text-muted flex-shrink-0 mt-1"></i>
                <div>
                  <h3 class="h6 fw-semibold mb-1">
                    Visitor analytics{" "}
                    <span
                      class="badge text-bg-secondary fw-normal"
                      style="font-size: 0.65rem"
                    >
                      Pro
                    </span>
                  </h3>
                  <p class="text-muted small mb-0">
                    See how many people visit your page, which links they click,
                    and where they come from.
                  </p>
                </div>
              </div>
            </div>
            <div class="col-sm-6 col-lg-4">
              <div class="d-flex gap-3">
                <i class="bi bi-envelope fs-4 text-muted flex-shrink-0 mt-1"></i>
                <div>
                  <h3 class="h6 fw-semibold mb-1">
                    Contact form{" "}
                    <span
                      class="badge text-bg-secondary fw-normal"
                      style="font-size: 0.65rem"
                    >
                      Pro
                    </span>
                  </h3>
                  <p class="text-muted small mb-0">
                    Let visitors reach you directly from your page without
                    exposing your email.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free vs Pro teaser */}
      <section class="py-5 bg-body-secondary">
        <div class="container text-center" style="max-width: 560px">
          <h2 class="h4 fw-semibold mb-2">Free plan, or go Pro</h2>
          <p class="text-muted mb-4">
            The free plan is genuinely useful — one page, AI chat editing,
            instant publishing. Pro ($12/mo) adds embeds, analytics, contact
            form, and more AI turns.
          </p>
          <a
            class="btn btn-outline-primary"
            href="/pricing"
            onClick={go("/pricing")}
          >
            Compare plans
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section class="py-5">
        <div class="container text-center" style="max-width: 560px">
          <h2 class="h4 fw-semibold mb-2">Ready to build your page?</h2>
          <p class="text-muted mb-4">
            It takes about two minutes. No card, no setup — just describe
            yourself and let the AI handle the rest.
          </p>
          <a
            class="btn btn-primary btn-lg"
            href="/signup"
            onClick={go("/signup")}
          >
            Create your page &mdash; free
          </a>
        </div>
      </section>
    </PublicLayout>
  );
}
